// Web Push on this device (ADR 0015). The subscription itself is the opt-in: one per browser profile and account.
// The server only sends to allowlisted push services; payloads carry a title, a short body and an in-app link — never
// note or comment text. Delivery is best effort: the in-app inbox and e-mail stay the reliable channels.
import { reactive } from 'vue';
import { api } from '@/api/client';
import { TransportError } from './ports';

export type PushState = 'unsupported' | 'ios_install' | 'unavailable' | 'denied' | 'off' | 'on' | 'busy';
export const push = reactive<{ state: PushState; error: string }>({ state: 'unavailable', error: '' });

let publicKey: string | null = null;

const isIos = () => /iphone|ipad|ipod/i.test(navigator.userAgent) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
const standalone = () => (window.matchMedia && window.matchMedia('(display-mode: standalone)').matches) || (navigator as unknown as { standalone?: boolean }).standalone === true;
export const pushSupported = (): boolean => typeof window !== 'undefined' && 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;

function keyBytes(b64url: string): Uint8Array<ArrayBuffer> {
  const b64 = b64url.replace(/-/g, '+').replace(/_/g, '/') + '='.repeat((4 - (b64url.length % 4)) % 4);
  const raw = atob(b64), out = new Uint8Array(new ArrayBuffer(raw.length));
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}
const sameKey = (s: PushSubscription, key: string) => {
  const k = s.options && s.options.applicationServerKey ? new Uint8Array(s.options.applicationServerKey) : null;
  const want = keyBytes(key);
  return !!k && k.length === want.length && k.every((b, i) => b === want[i]);
};
const body = (s: PushSubscription) => { const j = s.toJSON() as { endpoint?: string; keys?: { p256dh?: string; auth?: string } }; return { endpoint: j.endpoint, keys: { p256dh: j.keys?.p256dh, auth: j.keys?.auth } }; };
/** The active service worker registration, or null (never waits forever: `ready` never resolves without a worker). */
async function registration(waitMs = 0): Promise<ServiceWorkerRegistration | null> {
  const r = await navigator.serviceWorker.getRegistration('/');
  if (r && r.active) return r;
  if (!waitMs) return null;
  return Promise.race([navigator.serviceWorker.ready, new Promise<null>(res => setTimeout(() => res(null), waitMs))]);
}

/** Reads the server configuration and this device's state; re-registers an existing subscription for this account. */
export async function initPush(): Promise<void> {
  if (!pushSupported()) { push.state = isIos() && !standalone() ? 'ios_install' : 'unsupported'; return; }
  try { const c = await api<{ enabled: boolean; public_key: string | null }>('GET', '/push/config'); publicKey = c.enabled ? c.public_key : null; }
  catch { publicKey = null; }
  if (!publicKey) { push.state = 'unavailable'; return; }
  if (Notification.permission === 'denied') { push.state = 'denied'; return; }
  const reg = await registration(10_000);
  if (!reg) { push.state = 'unsupported'; return; }
  let sub = await reg.pushManager.getSubscription();
  if (sub && !sameKey(sub, publicKey)) { await sub.unsubscribe().catch(() => undefined); sub = null; } // server keys rotated
  if (sub && Notification.permission === 'granted') {
    push.state = 'on';
    await api('PUT', '/me/push-subscriptions', body(sub)).catch(() => undefined); // binds the device to the signed-in account
  } else push.state = 'off';
}

export async function enablePush(): Promise<void> {
  if (!publicKey || push.state === 'busy') return;
  push.state = 'busy'; push.error = '';
  try {
    const perm = await Notification.requestPermission();
    if (perm !== 'granted') { push.state = perm === 'denied' ? 'denied' : 'off'; return; }
    const reg = await registration(10_000);
    if (!reg) throw new Error('no service worker');
    const sub = (await reg.pushManager.getSubscription()) || await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: keyBytes(publicKey) });
    await api('PUT', '/me/push-subscriptions', body(sub));
    push.state = 'on';
  } catch (e) {
    push.state = 'off';
    push.error = e instanceof TransportError && e.kind === 'network' ? 'This action needs a connection' : 'Could not turn on notifications';
  }
}

export async function disablePush(): Promise<void> {
  if (push.state === 'busy') return;
  push.state = 'busy'; push.error = '';
  try {
    const reg = await registration();
    const sub = reg ? await reg.pushManager.getSubscription() : null;
    if (sub) { await api('DELETE', '/me/push-subscriptions', { endpoint: sub.endpoint }).catch(() => undefined); await sub.unsubscribe(); }
    push.state = 'off';
  } catch { push.state = 'on'; push.error = 'Could not turn off notifications'; }
}

/** On sign-out: this device must stop receiving the account's notifications (shared computers). */
export async function forgetPush(): Promise<void> {
  if (!pushSupported()) return;
  try {
    const reg = await registration(); // never blocks sign-out
    const sub = reg ? await reg.pushManager.getSubscription() : null;
    if (sub) { await api('DELETE', '/me/push-subscriptions', { endpoint: sub.endpoint }).catch(() => undefined); await sub.unsubscribe(); }
  } catch { /* best effort */ }
  push.state = 'off';
}

/** Asks the server to send a test notification to this account's devices (Profile). */
export async function sendTestPush(): Promise<{ ok: boolean; error?: string }> {
  try { await api('POST', '/me/push-subscriptions/test'); return { ok: true }; }
  catch (e) {
    const te = e instanceof TransportError ? e : null;
    return { ok: false, error: te?.kind === 'network' ? 'This action needs a connection' : te?.code === 'push_no_subscriptions' ? 'Turn on push notifications on this device first' : te?.code === 'rate_limited' ? 'Too many attempts; try again later' : 'Could not send a test notification' };
  }
}
